import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { uploadChatAttachment } from '../services/storage';
import type { ChatAttachment } from '../services/aiChat';
import { validateAttachmentFiles } from '../components/ai/AttachmentPickerButton';
import { useIdioma } from '../i18n/idioma';
import type { PendingAttachment } from '../components/ai/AttachmentThumb';

/**
 * Shared state + behavior for the paperclip + thumbnail strip used by both
 * AIChatPanel (drawer) and AIChatView (full page).
 *
 * Callers pass a `getConversationId()` closure rather than an id directly so
 * uploads always use the latest conversation id — `activeConversation` may
 * be created lazily on the same tick as the send.
 */
export function useChatAttachments(getConversationId: () => string | null) {
  const { t } = useIdioma();
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const pendingRef = useRef<PendingAttachment[]>([]);
  const cancelledRef = useRef<Set<string>>(new Set());

  useEffect(() => { pendingRef.current = pending; }, [pending]);

  // Clean up blob: URLs on unmount
  useEffect(() => {
    return () => {
      for (const p of pendingRef.current) {
        if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
      }
    };
  }, []);

  const addFiles = useCallback((files: File[]) => {
    const valid = validateAttachmentFiles(files, pendingRef.current.length);
    if (!valid.length) return;

    const newItems: PendingAttachment[] = valid.map(file => ({
      localId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
    }));

    setPending(prev => [...prev, ...newItems]);

    // Kick off uploads in parallel. Each resolves independently.
    for (const item of newItems) {
      const convId = getConversationId() ?? 'scratch';
      uploadChatAttachment(item.file, convId)
        .then(remoteUrl => {
          if (cancelledRef.current.has(item.localId)) return;
          setPending(prev =>
            prev.map(p =>
              p.localId === item.localId ? { ...p, status: 'ready' as const, remoteUrl } : p
            )
          );
        })
        .catch(err => {
          if (cancelledRef.current.has(item.localId)) return;
          console.error('[useChatAttachments] upload failed:', err);
          toast.error(t('chat.adjunto.fallo', { archivo: item.file.name }));
          setPending(prev =>
            prev.map(p =>
              p.localId === item.localId
                ? { ...p, status: 'error' as const, errorMessage: err?.message || 'Upload failed' }
                : p
            )
          );
        });
    }
  }, [getConversationId, t]);

  const removeOne = useCallback((localId: string) => {
    cancelledRef.current.add(localId);
    setPending(prev => {
      const target = prev.find(p => p.localId === localId);
      if (target?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p.localId !== localId);
    });
  }, []);

  const clearAll = useCallback(() => {
    for (const p of pendingRef.current) {
      cancelledRef.current.add(p.localId);
      if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
    }
    setPending([]);
  }, []);

  /**
   * Wait for all in-flight uploads to settle, then return the ready attachments
   * as a ChatAttachment[] suitable for passing to sendMessage. Drops failed uploads.
   */
  const takeReadyAttachments = useCallback(async (): Promise<ChatAttachment[]> => {
    // Wait up to 30s for any still-uploading entries to settle.
    const deadline = Date.now() + 30_000;
    while (pendingRef.current.some(p => p.status === 'uploading') && Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 100));
    }

    const ready = pendingRef.current.filter(p => p.status === 'ready' && p.remoteUrl);
    const attachments: ChatAttachment[] = ready.map(p => ({
      url: p.remoteUrl!,
      mimeType: p.file.type,
      name: p.file.name,
      sizeBytes: p.file.size,
    }));

    if (ready.length !== pendingRef.current.length) {
      const failedCount = pendingRef.current.length - ready.length;
      if (failedCount > 0) toast.error(t('chat.adjunto.omitidos', { count: failedCount }));
    }

    clearAll();
    return attachments;
  }, [clearAll, t]);

  const hasPending = pending.length > 0;
  const allReady = pending.every(p => p.status === 'ready');

  return {
    pending,
    hasPending,
    allReady,
    addFiles,
    removeOne,
    clearAll,
    takeReadyAttachments,
  };
}
